import { useEffect, useCallback } from 'react';
import { FILE_TYPE } from 'variables/constants';
import Card from 'components/card/Card';
import { Text, SimpleGrid, Box, Tooltip, Checkbox } from '@chakra-ui/react';
import { FormLabel } from '@chakra-ui/react';
import type { LOAInformationProps } from './types';
import { useAppSelector, useAppDispatch } from 'hooks';
import { getCarrierSettings } from 'views/shared/MethodOfPayment/helpers';
import {
  AUTO_PAYMENT_OPTIONS,
  PROPERTY_PAYMENT_OPTIONS,
} from 'views/shared/MethodOfPayment/configs';
import SharedMessage from 'components/fields/shared/Message/SharedMessage';
import SharedFormInput from 'components/fields/shared/FormInput/SharedFormInput';

interface SharedLOAInformationProps<
  TFormActions,
> extends LOAInformationProps<TFormActions> {}

export const SharedLOAInformation = <
  TFormActions extends {
    changePolicyInformation: (payload: { name: string; value: any }) => any;
    changeMethodOfPayment: (payload: { name: string; value: any }) => any;
    removeMissingMandatoryField: (field: string) => any;
    addMissingMandatoryField: any;
    addRemark: any;
    removeRemark: any;
    updateRemark: any;
    addFile: any;
    removeFile: any;
  },
>({
  formStateKey,
  formActions,
  sectionId,
}: SharedLOAInformationProps<TFormActions>) => {
  const dispatch = useAppDispatch();

  const removeBillingMandatoryFields = useCallback(() => {
    dispatch(formActions.removeMissingMandatoryField('Payment Period'));
    dispatch(formActions.removeMissingMandatoryField('Payment Day of Month'));
    dispatch(formActions.removeMissingMandatoryField('Payment Date'));
    dispatch(formActions.removeMissingMandatoryField('Payment Method'));
    dispatch(formActions.removeMissingMandatoryField('Down Payment'));
    dispatch(formActions.removeMissingMandatoryField('Down Payment Amount'));
    dispatch(formActions.removeMissingMandatoryField('Down Payment Date'));
    dispatch(formActions.removeMissingMandatoryField('Down Payment Method'));
    dispatch(formActions.removeMissingMandatoryField('Installment Due Date'));
    dispatch(formActions.removeMissingMandatoryField('Interest'));
  }, [dispatch, formActions]);

  const loaData = useAppSelector(
    (state) => state[formStateKey].data.policy_information,
  );

  const insuranceCompany = useAppSelector((state) => {
    if (formStateKey === 'autoPersonalForm') {
      return state[formStateKey].data.company_and_broker_information
        ?.insurance_company;
    }
    return state[formStateKey].data.policy_information?.insurance_company;
  });

  const methodOfPaymentData = useAppSelector(
    (state) => state[formStateKey].data.method_of_payment,
  );

  const isLOA = useAppSelector(
    (state) => state[formStateKey].data?.policy_information?.is_loa || false,
  );

  const effectiveDate = useAppSelector(
    (state) => state[formStateKey].data.policy_period?.effective_date,
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    dispatch(formActions.changePolicyInformation({ name, value }));
  };

  const onRequireBillingChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked;

    dispatch(
      formActions.changeMethodOfPayment({
        name: 'require_billing',
        value: isChecked,
      }),
    );

    if (!isChecked && !isBillingRequired) {
      removeBillingMandatoryFields();
    }
  };

  const paymentOptions =
    formStateKey === 'autoPersonalForm'
      ? AUTO_PAYMENT_OPTIONS
      : PROPERTY_PAYMENT_OPTIONS;
  const carrierSettings = getCarrierSettings(
    insuranceCompany || '',
    paymentOptions,
  );
  const isBillingRequired = carrierSettings?.loa_billing_required || false;
  const requireBillingChecked =
    isBillingRequired || methodOfPaymentData.require_billing;

  useEffect(() => {
    if (isLOA === false) {
      dispatch(formActions.removeMissingMandatoryField('Prior Broker (LOA)'));
      dispatch(
        formActions.removeMissingMandatoryField('Prior Policy Number (LOA)'),
      );
      dispatch(formActions.removeMissingMandatoryField('Broker Code'));
    }
  }, [isLOA, dispatch, formActions]);

  useEffect(() => {
    if (isLOA && !requireBillingChecked) {
      removeBillingMandatoryFields();
    }
  }, [isLOA, requireBillingChecked, removeBillingMandatoryFields]);

  if (!isLOA) return null;

  return (
    <Card mb="20px" id="sectionLOAInformation">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        LOA Information
      </Text>

      <SimpleGrid columns={3} spacing="20px">
        <SharedFormInput
          formStateKey={formStateKey}
          formActions={formActions}
          section={sectionId}
          label="Prior Broker"
          subLabel="LOA"
          name="loa_prior_broker"
          value={loaData.loa_prior_broker || ''}
          onChange={handleInputChange}
          required={isLOA}
        />

        <SharedFormInput
          formStateKey={formStateKey}
          formActions={formActions}
          section={sectionId}
          label="Prior Policy Number"
          subLabel="LOA"
          name="loa_prior_policy_number"
          value={loaData.loa_prior_policy_number || ''}
          onChange={handleInputChange}
          required={isLOA}
        />
        <div>
          <FormLabel ml="0">Policy Renewal Documents</FormLabel>
          <SharedMessage
            formStateKey={formStateKey}
            formActions={formActions}
            section={sectionId}
            validationId="prior_policy_documents"
            storageKey="policy_information"
            index={1}
            file={FILE_TYPE.TYPE_PRIOR_POLICY_DOCUMENTS}
            manual={{
              message: effectiveDate
                ? `Please upload Policy Renewal Documents. The renewal effective date must match the policy effective date (${effectiveDate}).`
                : 'Please upload Policy Renewal Documents',
              show: isLOA,
            }}
            hideRemarks={true}
            compact={true}
          />
        </div>
      </SimpleGrid>

      <Box mt="20px">
        <Tooltip
          label={
            isBillingRequired
              ? `Billing is required for ${insuranceCompany} carrier`
              : ''
          }
          isDisabled={!isBillingRequired}
          placement="top"
        >
          <Checkbox
            isChecked={requireBillingChecked}
            onChange={onRequireBillingChange}
            isDisabled={isBillingRequired}
          >
            <Text fontWeight="semibold" color="gray.600">
              Require Billing Information
            </Text>
          </Checkbox>
        </Tooltip>
      </Box>
    </Card>
  );
};
